import { useEffect, useState } from 'react'
import {
  Users,
  BarChart3,
  Settings,
  Loader2,
  AlertCircle,
  DollarSign,
  Activity,
  Save,
} from 'lucide-react'
import { admin, type User, type UsageSummary } from '../../lib/api'

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = 'users' | 'usage' | 'settings'

const TABS: Array<{ id: Tab; label: string; icon: React.ReactNode }> = [
  { id: 'users', label: 'Users', icon: <Users size={15} /> },
  { id: 'usage', label: 'Usage', icon: <BarChart3 size={15} /> },
  { id: 'settings', label: 'Settings', icon: <Settings size={15} /> },
]

// ─── Users Tab ────────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  useEffect(() => {
    admin.listUsers().then(setUsers).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : 'Failed to load users')
    }).finally(() => setLoading(false))
  }, [])

  const handleRoleChange = async (userId: string, role: 'admin' | 'user') => {
    setUpdatingId(userId)
    try {
      const updated = await admin.updateUser(userId, role)
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)))
    } catch (e) {
      console.error('Role update failed', e)
    } finally {
      setUpdatingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={22} className="animate-spin text-alk-gray" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200">
        <AlertCircle size={14} className="text-alk-red" />
        <span className="text-sm text-alk-red">{error}</span>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="py-2.5 px-4 text-left section-label">Email</th>
            <th className="py-2.5 px-4 text-left section-label">Role</th>
            <th className="py-2.5 px-4 text-left section-label">Last Login</th>
            <th className="py-2.5 px-4 text-left section-label">Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 px-4 text-sm text-alk-black">{user.email}</td>
              <td className="py-3 px-4">
                {updatingId === user.id ? (
                  <Loader2 size={14} className="animate-spin text-alk-gray" />
                ) : (
                  <select
                    value={user.role}
                    onChange={(e) =>
                      void handleRoleChange(user.id, e.target.value as 'admin' | 'user')
                    }
                    className="text-sm border border-gray-300 bg-white px-2 py-1 text-alk-black focus:outline-none focus:border-alk-red"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                )}
              </td>
              <td className="py-3 px-4 text-xs text-alk-gray">
                {user.lastLogin
                  ? new Date(user.lastLogin).toLocaleDateString()
                  : 'Never'}
              </td>
              <td className="py-3 px-4 text-xs text-alk-gray">
                {new Date(user.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {users.length === 0 && (
        <div className="text-center py-10 text-sm text-alk-gray">No users found.</div>
      )}
    </div>
  )
}

// ─── Usage Tab ────────────────────────────────────────────────────────────────

function UsageTab() {
  const [usage, setUsage] = useState<UsageSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const today = new Date().toISOString().split('T')[0]
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]

  const [from, setFrom] = useState(thirtyDaysAgo)
  const [to, setTo] = useState(today)

  const fetchUsage = () => {
    setLoading(true)
    setError(null)
    admin.getUsage(from, to).then(setUsage).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : 'Failed to load usage')
    }).finally(() => setLoading(false))
  }

  useEffect(() => { fetchUsage() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      {/* Date range picker */}
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="section-label block mb-1">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="input-field w-auto"
          />
        </div>
        <div>
          <label className="section-label block mb-1">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="input-field w-auto"
          />
        </div>
        <button onClick={fetchUsage} className="btn-primary flex items-center gap-2">
          <Activity size={14} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-8 justify-center">
          <Loader2 size={18} className="animate-spin text-alk-gray" />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200">
          <AlertCircle size={14} className="text-alk-red" />
          <span className="text-sm text-alk-red">{error}</span>
        </div>
      )}

      {usage && !loading && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="card">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={15} className="text-alk-red" />
                <span className="section-label">Total Cost</span>
              </div>
              <div className="font-display text-2xl font-bold text-alk-black">
                ${usage.totalCostUsd.toFixed(2)}
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-2 mb-1">
                <Activity size={15} className="text-alk-red" />
                <span className="section-label">Total Jobs</span>
              </div>
              <div className="font-display text-2xl font-bold text-alk-black">
                {usage.totalJobs}
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={15} className="text-alk-gray" />
                <span className="section-label">Avg per Job</span>
              </div>
              <div className="font-display text-2xl font-bold text-alk-black">
                ${usage.totalJobs > 0 ? (usage.totalCostUsd / usage.totalJobs).toFixed(2) : '0.00'}
              </div>
            </div>
          </div>

          {/* By service */}
          {usage.byService.length > 0 && (
            <div>
              <div className="section-label mb-3">By Service</div>
              <div className="card p-0 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="py-2 px-4 text-left section-label">Service</th>
                      <th className="py-2 px-4 text-right section-label">Calls</th>
                      <th className="py-2 px-4 text-right section-label">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.byService.map((s) => (
                      <tr key={s.service} className="border-b border-gray-100">
                        <td className="py-2.5 px-4 text-sm text-alk-black">{s.service}</td>
                        <td className="py-2.5 px-4 text-sm text-alk-gray text-right">{s.calls}</td>
                        <td className="py-2.5 px-4 text-sm font-medium text-alk-black text-right">
                          ${s.costUsd.toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* By user */}
          {usage.byUser.length > 0 && (
            <div>
              <div className="section-label mb-3">By User</div>
              <div className="card p-0 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="py-2 px-4 text-left section-label">User</th>
                      <th className="py-2 px-4 text-right section-label">Jobs</th>
                      <th className="py-2 px-4 text-right section-label">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.byUser.map((u) => (
                      <tr key={u.userId} className="border-b border-gray-100">
                        <td className="py-2.5 px-4 text-sm text-alk-black">{u.email}</td>
                        <td className="py-2.5 px-4 text-sm text-alk-gray text-right">{u.jobs}</td>
                        <td className="py-2.5 px-4 text-sm font-medium text-alk-black text-right">
                          ${u.costUsd.toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────

function SettingsTab() {
  const [settings, setSettings] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [edits, setEdits] = useState<Record<string, string>>({})

  useEffect(() => {
    admin.getSettings().then((s) => {
      setSettings(s)
      setEdits(s)
    }).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : 'Failed to load settings')
    }).finally(() => setLoading(false))
  }, [])

  const handleSave = async (key: string) => {
    setSaving(key)
    try {
      await admin.updateSetting(key, edits[key] ?? '')
      setSettings((prev) => ({ ...prev, [key]: edits[key] ?? '' }))
    } catch (e) {
      console.error('Save failed', e)
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={22} className="animate-spin text-alk-gray" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200">
        <AlertCircle size={14} className="text-alk-red" />
        <span className="text-sm text-alk-red">{error}</span>
      </div>
    )
  }

  const keys = Object.keys(settings)

  return (
    <div className="space-y-3">
      {keys.length === 0 && (
        <div className="text-center py-10 text-sm text-alk-gray">
          No settings configured.
        </div>
      )}
      {keys.map((key) => (
        <div key={key} className="flex items-center gap-3">
          <label className="text-sm font-display uppercase tracking-wide text-alk-black w-48 flex-shrink-0">
            {key}
          </label>
          <input
            type="text"
            value={edits[key] ?? ''}
            onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
            className="input-field flex-1"
          />
          <button
            onClick={() => void handleSave(key)}
            disabled={saving === key || edits[key] === settings[key]}
            className="btn-primary flex items-center gap-1.5 py-2 flex-shrink-0"
          >
            {saving === key ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Save size={13} />
            )}
            Save
          </button>
        </div>
      ))}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function AdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>('users')

  return (
    <div>
      <div className="mb-8">
        <h1 className="page-title">Admin Panel</h1>
        <p className="text-sm text-alk-gray mt-1">
          Manage users, monitor usage, and configure system settings.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-display uppercase tracking-wide border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-alk-red text-alk-red'
                : 'border-transparent text-alk-gray hover:text-alk-black'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="card p-0 overflow-hidden">
        <div className="p-5">
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'usage' && <UsageTab />}
          {activeTab === 'settings' && <SettingsTab />}
        </div>
      </div>
    </div>
  )
}
